import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, LogIn } from 'lucide-react';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();

  // Clean up any invalid tokens on component mount
  useEffect(() => {
    // If there's no user but there's a token, clean it up
    const token = localStorage.getItem('token');
    if (token && !user) {
      localStorage.removeItem('token');
    }
  }, [user]);

  // Handle redirection when user state changes
  useEffect(() => {
    if (user) {
      if (user.role === 'rh') {
        navigate('/rh', { replace: true });
      } else if (user.role === 'manager') {
        if (user.company === 'Horizon Solution') {
          navigate('/manager-horizon', { replace: true });
        } else {
          navigate('/manager', { replace: true });
        }
      } else if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        // For unknown roles, log them out
        logout();
        toast.info('Rôle non reconnu. Veuillez contacter l\'administrateur.');
      }
    }
  }, [user, navigate, logout]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    const result = await login(username, password);
    setLoading(false);

    if (result.success) {
      toast.success('Connexion réussie !');
      // La redirection sera gérée par le useEffect quand user se mettra à jour
    } else {
      toast.error(result.error || 'Erreur de connexion');
    }
  };

  return (
    <div className="login-container">
      <Card className="login-card">
        <CardHeader className="space-y-4">
          <CardTitle className="login-title">
            Recrutement Prysme
          </CardTitle>
          <CardDescription className="login-subtitle">
            Connectez-vous avec vos identifiants Prysme (prenom.nom) ou vos anciens identifiants
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="form-group">
              <Label htmlFor="username" className="form-label">
                Nom d'utilisateur
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="form-input"
                placeholder="prenom.nom ou ancien identifiant"
                required
              />
              <div className="mt-1 text-xs text-gray-500">
                Format Prysme : <span className="font-mono bg-gray-100 px-1 rounded">prenom.nom</span> 
                ou utilisez vos anciens identifiants
              </div>
            </div>
            
            <div className="form-group">
              <Label htmlFor="password" className="form-label">
                Mot de passe
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input pr-12"
                  placeholder="Entrez votre mot de passe"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 text-lg font-semibold"
              style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Connexion...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <LogIn size={20} />
                  <span>Se connecter</span>
                </div>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;